import { useGstWording } from "./useGstWording";
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { listPriceComponents, listPriceRules, type PriceComponent } from "../../api/priceList";
import { getInvoicePrefs } from "../../api/invoicePrefs";
import {
  cancelPromotion,
  createPromoCode,
  createPromotion,
  deactivatePromoCode,
  listPromoCodes,
  promoCodeState,
  promoOff,
  isPlanComponent,
  isPromotable,
  listPromoUses,
  promoUseTotals,
  termDaysOf,
  type PromoAudience,
  type PromoCode,
  lastDay,
  planPriceAt,
  promotionDetail,
  priceAfter,
  promotionsOf,
  promotionTitle,
  type PromotionRequest,
  type PromotionResult,
  type PromotionState,
  type PlanPrice,
  type PriceComponentLike,
} from "../../api/promotion";

/**
 * A plan as the pickers show it: its name, what it costs today, and -- once a
 * discount is typed -- what it would cost. Owner 2026-10-14: the operator must
 * see that ₹100 off a ₹150 plan leaves ₹50 before choosing it.
 */
function PlanLabel({ name, price, kind, value, forCode, maxOff, minBill, termDays, minTermDays }: {
  name: string;
  price: PlanPrice | undefined;
  kind: "percent" | "rupees" | "days";
  value: number;
  forCode?: boolean;
  maxOff?: number | undefined;
  minBill?: number | undefined;
  termDays?: number;
  minTermDays?: number | undefined;
}) {
  if (!price) return <>{name}</>;
  const belowMinimum = minBill !== undefined && price.gross < minBill;
  const tooShort = forCode && minTermDays !== undefined && (termDays ?? 0) !== minTermDays;
  const after =
    value > 0 && kind !== "days" && !(belowMinimum && !forCode) && !tooShort
      ? priceAfter(price.gross, kind, value, { maxOff, mayBeFree: !forCode && price.oneTime })
      : null;
  return (
    <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", py: 0.5 }}>
      <span>{name}</span>
      <Typography component="span" variant="body2" color="text.secondary">
        ₹{price.gross} today{price.festive ? " (festive price)" : ""}
        {after && !after.tooMuch && <> → {after.after === 0 ? "free" : `₹${after.after}`}</>}
        {kind === "days" && value > 0 && !tooShort && <> · +{value} days</>}
      </Typography>
      {belowMinimum && (
        <Typography component="span" variant="body2" color="warning.main">
          {forCode
            ? `₹${price.gross} alone is below the ₹${minBill} minimum bill; add-ons in the same bill may reach it.`
            : `Below the ₹${minBill} minimum; not eligible.`}
        </Typography>
      )}
      {tooShort && (
        <Typography component="span" variant="body2" color="warning.main">
          Not a {minTermDays}-day plan; the code does not apply.
        </Typography>
      )}
      {after?.tooMuch && (
        <Typography component="span" variant="body2" color="error">
          {forCode ? `The discount is the whole price; this plan would cost ₹1.` : `The discount is the whole price; not eligible (only a one-time fee may be free).`}
        </Typography>
      )}
      {forCode && price.festive && (
        <Typography component="span" variant="body2" color="warning.main">
          A code does not apply while the festive price runs.
        </Typography>
      )}
    </Box>
  );
}

const STATE: Record<PromotionState, { label: string; color: "info" | "success" | "default" }> = {
  scheduled: { label: "Scheduled", color: "info" },
  running: { label: "Running", color: "success" },
  ended: { label: "Ended", color: "default" },
  cancelled: { label: "Cancelled", color: "default" },
};

/** The day after `last`: the date the normal price returns. */
function dayAfter(last: string): string {
  const d = new Date(`${last}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * Plans › Promotions (owner 2026-10-14, design §9.17). A festive promotion is
 * a dated promotional price on chosen plans: from the first day to the last,
 * whoever pays for one of those plans -- new or renewing -- pays the festive
 * price; the day after, the normal price returns by itself. Add-ons keep their
 * price. Nothing is deleted; a cancelled promotion stops from tomorrow.
 */
/**
 * Promotions › Festive and Promotions › Promotion codes (owner 2026-10-14:
 * "Let the separate menu item promotion --> Festive and promotion -> promotion
 * code"). One screen, two sections: both need the same namespace, plans and
 * prices.
 */
export function PlansPromotions({ section = "festive" }: { section?: "festive" | "codes" }) {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  // Owner 2026-10-29: no word of GST for an unregistered or composition operator.
  const { speak } = useGstWording(ns);
  const qc = useQueryClient();

  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [last, setLast] = useState("");
  const [kind, setKind] = useState<"percent" | "rupees">("percent");
  const [value, setValue] = useState("");
  const [upTo, setUpTo] = useState("");
  const [festiveMin, setFestiveMin] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ key: string; result: PromotionResult } | null>(null);
  const [asking, setAsking] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);

  const components = useQuery({
    queryKey: ["promo-components", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => listPriceComponents(identity, ns, signal),
  });
  const rules = useQuery({
    queryKey: ["promo-rules", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => listPriceRules(identity, ns, signal),
  });
  const plans = (components.data ?? []).filter(isPlanComponent).sort((a, b) => a.name.localeCompare(b.name));
  // Owner 2026-10-14: a festive price may go on add-ons and one-time fees too.
  const offered = [
    ...plans,
    ...(components.data ?? []).filter((c) => isPromotable(c) && !isPlanComponent(c)).sort((a, b) => a.name.localeCompare(b.name)),
  ];
  const today = new Date();
  const prices = new Map(rules.data ? offered.map((c) => [c.id, planPriceAt(c, rules.data, today)] as const) : []);
  const maxOff = kind === "percent" && Number(upTo) > 0 ? Number(upTo) : undefined;
  const festiveMinimum = Number(festiveMin) > 0 ? Number(festiveMin) : undefined;
  const promotions = promotionsOf(rules.data ?? [], new Date());

  // Owner 2026-10-15: an item the offer cannot apply to is not "ticked but
  // refused" -- it is greyed out with its reason, never sent, and "All plans"
  // ticks only the eligible ones.
  const eligible = (c: PriceComponentLike): boolean => {
    const price = prices.get(c.id);
    if (!price) return true;
    if (festiveMinimum !== undefined && price.gross < festiveMinimum) return false;
    return !(Number(value) > 0 && priceAfter(price.gross, kind, Number(value), { maxOff, mayBeFree: price.oneTime }).tooMuch);
  };
  const chosen = offered.filter((c) => picked.has(c.id) && eligible(c)).map((c) => c.id);
  const eligiblePlans = plans.filter(eligible);

  const request = (asPreview: boolean): PromotionRequest => ({
    name: name.trim(),
    from,
    to: last ? dayAfter(last) : "",
    ...(kind === "percent" ? { percent: Number(value) } : { rupeesoff: Number(value) }),
    ...(maxOff ? { maxoff: maxOff } : {}),
    ...(festiveMinimum ? { minbill: festiveMinimum } : {}),
    componentids: [...chosen].sort(),
    ...(asPreview ? { preview: true } : {}),
  });
  const key = JSON.stringify(request(false));
  const previewed = preview?.key === key ? preview.result : null;

  const problems: string[] = [];
  if (!name.trim()) problems.push("Give the promotion a name.");
  if (!from || !last) problems.push("Choose the first and the last day.");
  if (!(Number(value) > 0)) problems.push(kind === "percent" ? "Give a percentage off." : "Give the rupees off.");
  if (chosen.length === 0) problems.push("Tick at least one eligible plan or add-on.");

  const choice = (c: PriceComponentLike) => (
    <FormControlLabel key={c.id} sx={{ alignItems: "flex-start" }}
      label={<PlanLabel name={c.name} price={prices.get(c.id)} kind={kind} value={Number(value)} maxOff={maxOff}
        minBill={festiveMinimum} />}
      disabled={!eligible(c)}
      control={<Checkbox inputProps={{ "aria-label": c.name }} checked={picked.has(c.id) && eligible(c)} onChange={(e) => setPicked((s) => {
        const n = new Set(s);
        if (e.target.checked) n.add(c.id);
        else n.delete(c.id);
        return n;
      })} />} />
  );

  const ask = useMutation<PromotionResult, Error>({
    mutationFn: () => createPromotion(identity, ns, request(true)),
    onSuccess: (result) => setPreview({ key, result }),
  });
  const create = useMutation<PromotionResult, Error>({
    mutationFn: () => createPromotion(identity, ns, request(false)),
    onSuccess: () => {
      setPreview(null);
      setPicked(new Set());
      setName("");
      void qc.invalidateQueries({ queryKey: ["promo-rules", ns] });
    },
  });
  const cancel = useMutation<unknown, Error, string>({
    mutationFn: (slug) => cancelPromotion(identity, ns, slug),
    onSuccess: () => {
      setAsking(null);
      void qc.invalidateQueries({ queryKey: ["promo-rules", ns] });
    },
  });
  const failure = ask.error ?? create.error ?? cancel.error;
  const notYet = failure instanceof ApiError && (failure.status === 404 || failure.status === 405) && !cancel.error;

  const namespacePicker = (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
        onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
      <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
        Open
      </Button>
    </Stack>
  );

  if (section === "codes")
    return (
      <Box sx={{ p: 2, maxWidth: 1000 }}>
        <Typography variant="h5" gutterBottom>
          Promotion codes
        </Typography>
        {namespacePicker}
        {components.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Plans not loaded</AlertTitle>
            {(components.error as Error).message}
          </Alert>
        )}
        <PromoCodes ns={ns} plans={plans} prices={prices} />
      </Box>
    );

  return (
    <Box sx={{ p: 2, maxWidth: 1000 }}>
      <Typography variant="h5" gutterBottom>
        Festive promotions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A promotion lowers the price of the plans you choose for the festival dates. Everyone who pays for one of those
        plans in that time — new subscribers and renewals — pays the festive price; the day after the last day the normal
        price returns by itself. Add-ons and one-time fees may take a festive price too. Prices change at 05:30 IST on the
        dates shown. Cancel a promotion and you may create a new one of the same name; orders already paid keep the
        price they were paid at.
      </Typography>
      {namespacePicker}

      {failure && (
        <Alert severity={notYet ? "info" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>{notYet ? "Promotions are not available yet" : "Not done"}</AlertTitle>
          {notYet ? "Promotions arrive with the next billing and gateway release." : failure.message}
        </Alert>
      )}
      {create.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Promotion scheduled</AlertTitle>
          {promotionTitle(create.data.slug)}: {create.data.plans.length} plan{create.data.plans.length === 1 ? "" : "s"},{" "}
          {create.data.from.slice(0, 10)} to {lastDay(create.data.to.slice(0, 10))}.
        </Alert>
      )}

      {promotions.length > 0 && (
        <Paper sx={{ mb: 2 }}>
          <Table size="small" aria-label="Promotions">
            <TableHead>
              <TableRow>
                <TableCell>Promotion</TableCell>
                <TableCell>First day</TableCell>
                <TableCell>Last day</TableCell>
                <TableCell>Plans</TableCell>
                <TableCell>Offer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {promotions.map((p) => {
                const detail = promotionDetail(p.slug, rules.data ?? [], components.data ?? []);
                return (
                <Fragment key={p.slug}>
                <TableRow>
                  <TableCell>{promotionTitle(p.slug)}</TableCell>
                  <TableCell>{p.from}</TableCell>
                  <TableCell>{lastDay(p.to)}</TableCell>
                  <TableCell>{p.plans}</TableCell>
                  <TableCell>{detail.offer || "Differs by plan"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATE[p.state].label} color={STATE[p.state].color} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" sx={{ mr: 1 }} aria-expanded={opened === p.slug}
                      onClick={() => setOpened(opened === p.slug ? null : p.slug)}>
                      {opened === p.slug ? "Hide details" : "Details"}
                    </Button>
                    {(p.state === "scheduled" || p.state === "running") &&
                      (asking === p.slug ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="warning" disabled={cancel.isPending}
                            onClick={() => cancel.mutate(p.slug)}>
                            Yes, cancel it
                          </Button>
                          <Button size="small" onClick={() => setAsking(null)}>
                            Keep it
                          </Button>
                        </Stack>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => { cancel.reset(); setAsking(p.slug); }}>
                          Cancel promotion
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
                {opened === p.slug && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ bgcolor: "action.hover" }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {promotionTitle(p.slug)}: {detail.offer || "a different discount on each plan"}, {p.from} to {lastDay(p.to)}
                        {speak ? " (prices include GST)" : ""}
                      </Typography>
                      <Table size="small" aria-label={`${promotionTitle(p.slug)} prices`}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Plan</TableCell>
                            <TableCell align="right">Normal price</TableCell>
                            <TableCell align="right">{promotionTitle(p.slug)} price</TableCell>
                            <TableCell align="right">Saving</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detail.plans.map((d) => (
                            <TableRow key={d.componentid}>
                              <TableCell>{d.name}</TableCell>
                              <TableCell align="right">₹{d.normal}</TableCell>
                              <TableCell align="right">₹{d.festive}</TableCell>
                              <TableCell align="right">₹{d.normal - d.festive}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          New promotion
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <TextField size="small" label="Name" placeholder="Diwali 2026" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField size="small" label="First day" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
          <TextField size="small" label="Last day" type="date" value={last} onChange={(e) => setLast(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
          <TextField select size="small" label="Off" value={kind} sx={{ minWidth: 110 }}
            onChange={(e) => setKind(e.target.value as "percent" | "rupees")}>
            <MenuItem value="percent">% off</MenuItem>
            <MenuItem value="rupees">₹ off</MenuItem>
          </TextField>
          <TextField size="small" label={kind === "percent" ? "Percent" : "Rupees"} type="number" value={value}
            sx={{ width: 110 }} onChange={(e) => setValue(e.target.value)} />
          {kind === "percent" && (
            <TextField size="small" label="Up to ₹ (optional)" type="number" value={upTo} sx={{ width: 150 }}
              onChange={(e) => setUpTo(e.target.value)} />
          )}
          <TextField size="small" label="Minimum price ₹ (optional)" type="number" value={festiveMin} sx={{ width: 190 }}
            onChange={(e) => setFestiveMin(e.target.value)} />
        </Stack>

        {components.isLoading && <Typography variant="body2">Loading plans…</Typography>}
        {components.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Could not load the plans</AlertTitle>
            {(components.error as Error).message}
          </Alert>
        )}
        {components.data && plans.length === 0 && (
          <Typography variant="body2">No plans are priced in {ns} yet.</Typography>
        )}
        {plans.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={<Checkbox checked={eligiblePlans.length > 0 && eligiblePlans.every((c) => picked.has(c.id))}
                indeterminate={eligiblePlans.some((c) => picked.has(c.id)) && !eligiblePlans.every((c) => picked.has(c.id))}
                onChange={(e) => setPicked((s) => {
                  const n = new Set(s);
                  for (const c of eligiblePlans) {
                    if (e.target.checked) n.add(c.id);
                    else n.delete(c.id);
                  }
                  return n;
                })} />}
              label={eligiblePlans.length < plans.length ? `All eligible plans (${eligiblePlans.length} of ${plans.length})` : "All plans"} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              {plans.map((c) => choice(c))}
            </Box>
          </Box>
        )}
        {offered.length > plans.length && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>
              Add-ons and one-time fees
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A one-time fee (activation, installation) may be made free with 100% off; an add-on keeps at least ₹1.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
              {offered.slice(plans.length).map((c) => choice(c))}
            </Box>
          </Box>
        )}

        {problems.length > 0 && picked.size + name.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {problems.join(" ")}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" disabled={problems.length > 0 || ask.isPending}
            onClick={() => { create.reset(); ask.mutate(); }}>
            Preview prices
          </Button>
          <Button variant="contained" disabled={!previewed || create.isPending} onClick={() => create.mutate()}>
            Schedule promotion
          </Button>
        </Stack>

        {previewed && (
          <Table size="small" aria-label="Festive prices" sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Normal</TableCell>
                <TableCell align="right">Festive</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previewed.plans.map((p) => (
                <TableRow key={p.componentid}>
                  <TableCell>{p.name}</TableCell>
                  <TableCell align="right">₹{p.normalgross}</TableCell>
                  <TableCell align="right">₹{p.festivegross}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

    </Box>
  );
}

/**
 * Promotion codes (owner 2026-10-14, design §9.18): a code the subscriber
 * types at online checkout -- Rs or % off, dated, all or chosen plans, once
 * per subscriber, an optional total cap, never on a plan at a festive price.
 */
function PromoCodes({ ns, plans, prices }: {
  ns: string;
  plans: Pick<PriceComponent, "id" | "name" | "labels">[];
  prices: Map<string, PlanPrice>;
}) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<"rupees" | "percent" | "days">("rupees");
  const [value, setValue] = useState("");
  const [audience, setAudience] = useState<"" | PromoAudience>("");
  const [minTerm, setMinTerm] = useState("");
  const [minCycles, setMinCycles] = useState("");
  const [opened, setOpened] = useState<string | null>(null);
  const [from, setFrom] = useState("");
  const [last, setLast] = useState("");
  const [cap, setCap] = useState("");
  const [upTo, setUpTo] = useState("");
  const [minBill, setMinBill] = useState("");
  const [allPlans, setAllPlans] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState<string | null>(null);

  const maxOff = kind === "percent" && Number(upTo) > 0 ? Number(upTo) : undefined;
  const minimum = Number(minBill) > 0 ? Number(minBill) : undefined;
  const minimumTooLow = kind === "rupees" && minimum !== undefined && Number(value) >= minimum;
  const minTermDays = Number(minTerm) > 0 ? Number(minTerm) : undefined;
  // Loyalty (owner 2026-10-15): payments already made in a row for a plan of
  // the same length; the extra days come on the next one.
  const minPaidCycles = Number(minCycles) > 0 ? Number(minCycles) : undefined;
  const tenureForNew = audience === "new" && minPaidCycles !== undefined;
  // A plan shorter than the code's minimum term can never take it: greyed
  // out and never sent. A plan below the minimum bill stays -- add-ons in the
  // same bill may reach it.
  const codeIneligible = (c: Pick<PriceComponent, "labels">) => minTermDays !== undefined && termDaysOf(c) !== minTermDays;

  const codes = useQuery({
    queryKey: ["promo-codes", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: () => listPromoCodes(identity, ns),
  });
  const create = useMutation<unknown, Error>({
    mutationFn: () =>
      createPromoCode(identity, ns, {
        code: code.trim(),
        ...(kind === "percent" ? { percent: Number(value) } : kind === "days" ? { bonusdays: Number(value) } : { rupeesoff: Number(value) }),
        from,
        to: last ? dayAfter(last) : "",
        ...(allPlans ? {} : { componentids: plans.filter((c) => picked.has(c.id) && !codeIneligible(c)).map((c) => c.id).sort() }),
        ...(audience ? { audience } : {}),
        ...(minTermDays ? { mintermdays: minTermDays } : {}),
        ...(minPaidCycles ? { minpaidcycles: minPaidCycles } : {}),
        ...(cap.trim() ? { maxuses: Number(cap) } : {}),
        ...(maxOff ? { maxoff: maxOff } : {}),
        ...(minimum ? { minbill: minimum } : {}),
      }),
    onSuccess: () => {
      setCode("");
      setValue("");
      setCap("");
      setUpTo("");
      setMinBill("");
      setMinTerm("");
      setMinCycles("");
      setAudience("");
      void qc.invalidateQueries({ queryKey: ["promo-codes", ns] });
    },
  });
  const deactivate = useMutation<unknown, Error, string>({
    mutationFn: (c) => deactivatePromoCode(identity, ns, c),
    onSuccess: () => {
      setAsking(null);
      void qc.invalidateQueries({ queryKey: ["promo-codes", ns] });
    },
  });
  const failure = create.error ?? deactivate.error ?? (codes.error as Error | null);
  const notYet = failure instanceof ApiError && (failure.status === 404 || failure.status === 405);
  const ready = code.trim().length >= 3 && Number(value) > 0 && from && last && (allPlans || plans.some((c) => picked.has(c.id) && !codeIneligible(c))) && !minimumTooLow && !tenureForNew;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Promotion codes
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A subscriber types the code when paying online and pays less, or gets extra days. Each subscriber can use a code
        once; you may also cap the total uses. A code may be for new or existing subscribers, need a minimum bill (plan and
        add-ons together), a plan of at least so many days, or a subscriber who has paid without a break for so many
        days (a renewal up to 7 days late still counts) — the loyalty reward. A code never applies on top of a festive price — one offer at a time. Staff never apply codes.
      </Typography>
      {failure && (
        <Alert severity={notYet ? "info" : "error"} sx={{ mb: 2 }}>
          <AlertTitle>{notYet ? "Promotion codes are not available yet" : "Not done"}</AlertTitle>
          {notYet ? "Promotion codes arrive with the next billing and gateway release." : failure.message}
        </Alert>
      )}
      {(codes.data ?? []).length > 0 && (
        <Table size="small" aria-label="Promotion codes" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Off</TableCell>
              <TableCell>First day</TableCell>
              <TableCell>Last day</TableCell>
              <TableCell>Plans</TableCell>
              <TableCell>Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(codes.data ?? []).map((p) => {
              const state = promoCodeState(p, new Date());
              return (
                <Fragment key={p.code}>
                <TableRow>
                  <TableCell><code>{p.code}</code></TableCell>
                  <TableCell>{promoOff(p)}</TableCell>
                  <TableCell>{p.validfrom.slice(0, 10)}</TableCell>
                  <TableCell>{lastDay(p.validto.slice(0, 10))}</TableCell>
                  <TableCell>{p.componentids?.length ? p.componentids.length : "All"}</TableCell>
                  <TableCell>{p.uses}{p.maxuses !== undefined ? ` of ${p.maxuses}` : ""}</TableCell>
                  <TableCell>
                    <Chip size="small" label={state} color={state === "active" ? "success" : state === "scheduled" ? "info" : "default"} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" sx={{ mr: 1 }} aria-expanded={opened === p.code}
                      onClick={() => setOpened(opened === p.code ? null : p.code)}>
                      {opened === p.code ? "Hide details" : "Details"}
                    </Button>
                    {p.active && state !== "expired" &&
                      (asking === p.code ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" color="warning" disabled={deactivate.isPending}
                            onClick={() => deactivate.mutate(p.code)}>
                            Yes, stop it
                          </Button>
                          <Button size="small" onClick={() => setAsking(null)}>Keep it</Button>
                        </Stack>
                      ) : (
                        <Button size="small" variant="outlined" onClick={() => { deactivate.reset(); setAsking(p.code); }}>
                          Deactivate
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
                {opened === p.code && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ bgcolor: "action.hover" }}>
                      <CodeUses ns={ns} code={p} />
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
        <TextField size="small" label="Code" placeholder="SAVE100" value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))} />
        <TextField select size="small" label="Off" value={kind} sx={{ minWidth: 110 }}
          onChange={(e) => setKind(e.target.value as "percent" | "rupees" | "days")}>
          <MenuItem value="rupees">₹ off</MenuItem>
          <MenuItem value="percent">% off</MenuItem>
          <MenuItem value="days">Extra days</MenuItem>
        </TextField>
        <TextField size="small" label={kind === "percent" ? "Code percent" : kind === "days" ? "Code extra days" : "Code rupees"}
          type="number" value={value}
          sx={{ width: 120 }} onChange={(e) => setValue(e.target.value)} />
        <TextField size="small" label="Code first day" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" label="Code last day" type="date" value={last} onChange={(e) => setLast(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }} />
        <TextField size="small" label="Total uses (optional)" type="number" value={cap} sx={{ width: 160 }}
          onChange={(e) => setCap(e.target.value)} />
        {kind === "percent" && (
          <TextField size="small" label="Code up to ₹ (optional)" type="number" value={upTo} sx={{ width: 170 }}
            onChange={(e) => setUpTo(e.target.value)} />
        )}
        <TextField size="small" label="Minimum bill ₹ (optional)" type="number" value={minBill} sx={{ width: 190 }}
          error={minimumTooLow} helperText={minimumTooLow ? "Must be more than the rupees off" : undefined}
          onChange={(e) => setMinBill(e.target.value)} />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
        <TextField select size="small" label="Who" value={audience} sx={{ minWidth: 200 }}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          onChange={(e) => setAudience(e.target.value as "" | PromoAudience)}>
          <MenuItem value="">Everyone</MenuItem>
          <MenuItem value="new">New subscribers</MenuItem>
          <MenuItem value="existing">Existing subscribers</MenuItem>
        </TextField>
        <TextField size="small" label="Plan length (days, optional)" type="number" value={minTerm} sx={{ width: 230 }}
          helperText="Exactly this length: 180 = 6-month plans only" onChange={(e) => setMinTerm(e.target.value)} />
        <TextField size="small" label="Already paid in a row, same plan length (times, optional)" type="number" value={minCycles}
          sx={{ width: 380 }} error={tenureForNew}
          helperText={tenureForNew ? "A new subscriber has not paid yet"
            : "6 = six monthly payments (or two 6-month ones); extra days on the next payment; up to 7 days late still counts"}
          onChange={(e) => setMinCycles(e.target.value)} />
      </Stack>
      <FormControlLabel control={<Checkbox checked={allPlans} onChange={(e) => setAllPlans(e.target.checked)} />}
        label="Every plan" />
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mb: 1 }}>
          {plans.map((c) => (
            <FormControlLabel key={c.id} sx={{ alignItems: "flex-start" }}
              label={<PlanLabel name={c.name} price={prices.get(c.id)} kind={kind} value={Number(value)} forCode
                maxOff={maxOff} minBill={minimum} termDays={termDaysOf(c)} minTermDays={minTermDays} />}
              disabled={allPlans || codeIneligible(c)}
              control={<Checkbox inputProps={{ "aria-label": `Code plan ${c.name}` }}
                checked={!codeIneligible(c) && (allPlans || picked.has(c.id))}
                disabled={allPlans || codeIneligible(c)}
                onChange={(e) => setPicked((s) => {
                  const n = new Set(s);
                  if (e.target.checked) n.add(c.id);
                  else n.delete(c.id);
                  return n;
                })} />} />
          ))}
      </Box>
      <Box>
        <Button variant="contained" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          Create code
        </Button>
      </Box>
    </Paper>
  );
}

/**
 * Who used a code (owner 2026-10-14: "reports who consume this promotion ...
 * and the total amount discount given"; design §9.19). The names come from the
 * operator's subscriber list when the gateway lets this login read it; else
 * the account id is shown.
 */
function CodeUses({ ns, code }: { ns: string; code: PromoCode }) {
  const identity = useIdentity();
  const uses = useQuery({
    queryKey: ["promo-uses", ns, code.code],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: () => listPromoUses(identity, ns, code.code),
  });
  const people = useQuery({
    queryKey: ["promo-people", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    staleTime: 60_000,
    queryFn: ({ signal }) => getInvoicePrefs(identity, ns, signal),
  });
  const byAccount = new Map((people.data?.subscribers ?? []).map((s) => [s.accountid.toLowerCase(), s]));
  const now = new Date();
  const rows = uses.data ?? [];
  const totals = promoUseTotals(rows, now);
  if (uses.isLoading) return <Typography variant="body2">Loading uses…</Typography>;
  if (uses.error)
    return (
      <Alert severity="error">
        <AlertTitle>Uses not loaded</AlertTitle>
        {(uses.error as Error).message}
      </Alert>
    );
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {code.code}: {promoOff(code)} — used {totals.used} time{totals.used === 1 ? "" : "s"}
        {totals.rupeesOff > 0 ? `, ₹${totals.rupeesOff} given off` : ""}
        {totals.bonusDays > 0 ? `, ${totals.bonusDays} extra days given` : ""}
        {totals.held > 0 ? `; ${totals.held} waiting for payment` : ""}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nobody has used this code yet.
        </Typography>
      ) : (
        <Table size="small" aria-label={`${code.code} uses`}>
          <TableHead>
            <TableRow>
              <TableCell>Subscriber</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell align="right">Normal price</TableCell>
              <TableCell align="right">Given</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>When</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((u) => {
              const who = byAccount.get(u.accountid.toLowerCase());
              const waiting = u.status === "RESERVED";
              return (
                <TableRow key={u.id}>
                  <TableCell>{who ? `${who.name || who.username} · ${who.mobile || who.username}` : u.accountid}</TableCell>
                  <TableCell>{u.plan}</TableCell>
                  <TableCell align="right">₹{u.normalgross}</TableCell>
                  <TableCell align="right">{u.bonusdays > 0 ? `${u.bonusdays} extra days` : `₹${u.rupeesoff} off`}</TableCell>
                  <TableCell>{waiting ? (new Date(u.expiresat) > now ? "Waiting for payment" : "Not paid") : "Paid"}</TableCell>
                  <TableCell>{u.at.slice(0, 16).replace("T", " ")} UTC</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
